import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface SidebarProps {
    activeTab: 'chat' | 'select' | 'conversations' | 'characters';
    setActiveTab: (tab: 'chat' | 'select' | 'conversations' | 'characters') => void;
    sidebarExpanded: boolean;
    setSidebarExpanded: (expanded: boolean) => void;
    onShowLogin: (mode?: 'login' | 'register') => void;
}

const Sidebar: React.FC<SidebarProps> = ({
    activeTab,
    setActiveTab,
    sidebarExpanded,
    onShowLogin
}) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 1024);

    // Determine current categories based on the route
    const getCurrentCategories = () => {
        const path = location.pathname;
        if (path === '/female-realistic') return { gender: 'female', style: 'realistic' };
        if (path === '/female-anime') return { gender: 'female', style: 'anime' };
        if (path === '/male-realistic') return { gender: 'male', style: 'realistic' };
        if (path === '/male-anime') return { gender: 'male', style: 'anime' };
        // Legacy support
        if (path === '/girls') return { gender: 'female', style: 'realistic' };
        if (path === '/anime') return { gender: 'female', style: 'anime' };
        if (path === '/guys') return { gender: 'male', style: 'realistic' };
        return { gender: null, style: null };
    };

    const currentCategories = getCurrentCategories();
    
    // Handle window resize
    React.useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 1024);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // Get responsive positioning
    const getSidebarStyle = () => {
        if (sidebarExpanded) {
            // When expanded, extend all the way to bottom (overlapping bottom nav)
            return {
                top: isDesktop ? '64px' : '120px', // Start below top bar
                bottom: '0', // Extend to very bottom
                height: 'auto' // Let it fill the space
            };
        } else {
            // When collapsed, normal height
            return {
                top: isDesktop ? '64px' : '120px',
                height: isDesktop ? 'calc(100vh - 64px)' : 'calc(100vh - 120px)'
            };
        }
    };

    return (
        <div 
          className={`fixed left-0 glass-effect backdrop-blur-xl border-r border-dark-600/50 transition-all duration-500 ease-in-out shadow-2xl ${
            sidebarExpanded 
                ? 'w-72 translate-x-0 z-[60]' 
                : 'w-20 -translate-x-full lg:translate-x-0 z-30'
            }`}
          style={getSidebarStyle()}
        >
            {/* Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-dark-800/90 via-dark-900/95 to-dark-900/98"></div>
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-accent-purple/10 to-transparent"></div>

            <div className="relative h-full flex flex-col">

                {/* Category Navigation Items */}
                <nav className={`flex-1 space-y-3 ${sidebarExpanded ? 'px-6 pt-6' : 'px-2 pt-6'}`}>
                    <button
                        onClick={() => navigate('/female-realistic')}
                        className={`group font-medium transition-all duration-300 flex items-center relative overflow-hidden ${sidebarExpanded
                            ? 'w-full p-4 rounded-xl gap-4'
                            : 'w-12 h-12 rounded-xl mx-auto justify-center'
                            } ${(currentCategories.gender === 'female' && currentCategories.style === 'realistic')
                                ? 'bg-gradient-to-r from-accent-purple/20 to-accent-pink/20 text-accent-purple border border-accent-purple/30 shadow-lg'
                                : 'text-dark-300 hover:text-white hover:bg-dark-700/50'
                            }`}
                    >
                        <div className="w-6 h-6 flex items-center justify-center">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z" />
                            </svg>
                        </div>
                        {sidebarExpanded && <span>Girls</span>}
                        {(currentCategories.gender === 'female' && currentCategories.style === 'realistic') && sidebarExpanded && (
                            <div className="absolute right-3 w-2 h-2 bg-accent-purple rounded-full animate-pulse"></div>
                        )}
                    </button>

                    <button
                        onClick={() => navigate('/female-anime')}
                        className={`group font-medium transition-all duration-300 flex items-center relative overflow-hidden ${sidebarExpanded
                            ? 'w-full p-4 rounded-xl gap-4'
                            : 'w-12 h-12 rounded-xl mx-auto justify-center'
                            } ${(currentCategories.gender === 'female' && currentCategories.style === 'anime') ||
                                (currentCategories.gender === 'male' && currentCategories.style === 'anime')
                                ? 'bg-gradient-to-r from-accent-purple/20 to-accent-pink/20 text-accent-purple border border-accent-purple/30 shadow-lg'
                                : 'text-dark-300 hover:text-white hover:bg-dark-700/50'
                            }`}
                    >
                        <div className="w-6 h-6 flex items-center justify-center">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                        </div>
                        {sidebarExpanded && <span>Anime</span>}
                        {((currentCategories.gender === 'female' && currentCategories.style === 'anime') ||
                            (currentCategories.gender === 'male' && currentCategories.style === 'anime')) && sidebarExpanded && (
                            <div className="absolute right-3 w-2 h-2 bg-accent-purple rounded-full animate-pulse"></div>
                        )}
                    </button>

                    <button
                        onClick={() => navigate('/male-realistic')}
                        className={`group font-medium transition-all duration-300 flex items-center relative overflow-hidden ${sidebarExpanded
                            ? 'w-full p-4 rounded-xl gap-4'
                            : 'w-12 h-12 rounded-xl mx-auto justify-center'
                            } ${(currentCategories.gender === 'male' && currentCategories.style === 'realistic')
                                ? 'bg-gradient-to-r from-accent-purple/20 to-accent-pink/20 text-accent-purple border border-accent-purple/30 shadow-lg'
                                : 'text-dark-300 hover:text-white hover:bg-dark-700/50'
                            }`}
                    >
                        <div className="w-6 h-6 flex items-center justify-center">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z" />
                            </svg>
                        </div>
                        {sidebarExpanded && <span>Guys</span>}
                        {(currentCategories.gender === 'male' && currentCategories.style === 'realistic') && sidebarExpanded && (
                            <div className="absolute right-3 w-2 h-2 bg-accent-purple rounded-full animate-pulse"></div>
                        )}
                    </button>

                    {/* Divider */}
                    <div className="py-4">
                        <div className={`border-t border-dark-600/30 ${sidebarExpanded ? 'mx-0' : 'mx-2'}`}></div>
                    </div>

                    {user ? (
                        <button
                            onClick={logout}
                            className={`group font-medium transition-all duration-300 flex items-center text-dark-300 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 ${sidebarExpanded
                                ? 'w-full p-4 rounded-xl gap-4'
                                : 'w-12 h-12 rounded-xl mx-auto justify-center'
                                }`}
                        >
                            <div className="w-6 h-6 flex items-center justify-center">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                            </div>
                            {sidebarExpanded && <span>Logout</span>}
                        </button>
                    ) : (
                        <button
                            onClick={() => onShowLogin('login')}
                            className={`group font-medium transition-all duration-300 flex items-center text-dark-300 hover:text-accent-purple hover:bg-accent-purple/10 border border-transparent hover:border-accent-purple/20 ${sidebarExpanded
                                ? 'w-full p-4 rounded-xl gap-4'
                                : 'w-12 h-12 rounded-xl mx-auto justify-center'
                                }`}
                        >
                            <div className="w-6 h-6 flex items-center justify-center">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                            </div>
                            {sidebarExpanded && <span>Login</span>}
                        </button>
                    )}
                </nav>

                {/* Create AI Girl Button - Only show when expanded */}
                {sidebarExpanded && (
                    <div className="p-6">
                        <a
                            href="/create"
                            className="group w-full p-4 rounded-2xl font-semibold transition-all duration-300 flex items-center gap-4 relative overflow-hidden text-white shadow-xl hover:shadow-2xl transform hover:scale-[1.02]"
                            style={{ background: 'linear-gradient(to right, #f472b6, #e91e63)' }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <span className="text-xl relative z-10">💎</span>
                            <span className="relative z-10">Create AI Girl</span>
                        </a>
                    </div>
                )}

                {/* User Info at Bottom */}
                {sidebarExpanded && (
                    <div className="p-6 pt-0 border-t border-dark-600/30">
                        <div className="glass-effect rounded-2xl p-4 border border-dark-600/30 relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-accent-purple/5 to-accent-pink/5"></div>
                            <div className="relative flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-accent-purple via-accent-magenta to-accent-pink flex items-center justify-center shadow-lg">
                                    <span className="text-white text-lg font-bold">
                                        {user ? (user.full_name || user.email || 'U').charAt(0).toUpperCase() : 'G'}
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-white text-sm font-semibold truncate">
                                        {user ? (user.full_name || 'User') : 'Guest User'}
                                    </p>
                                    <p className="text-dark-400 text-xs truncate">
                                        {user ? user.email : 'No account required'}
                                    </p>
                                    <div className="flex items-center gap-1 mt-1">
                                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                        <span className="text-xs text-green-400 font-medium">Online</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Sidebar;